import { type ReactNode } from "react";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";

export type AnnouncementContext = {
  badge?: string;
  badgeIcon?: ReactNode;
  title: string;
  description: string;
  primaryButtonText: string;
  secondaryButtonText: string;
};

type Props = {
  context: AnnouncementContext;
  logoSrc: string;
  logoAlt: string;
  isSubmitting?: boolean;
  onAcknowledge: VoidFunction;
  onPrimaryAction: VoidFunction;
};

const DATA_TEST_ID = "announcement-task";

export const AnnouncementTaskView = ({
  context,
  logoSrc,
  logoAlt,
  isSubmitting = false,
  onAcknowledge,
  onPrimaryAction,
}: Props) => {
  const {
    badge,
    badgeIcon,
    title,
    description,
    primaryButtonText,
    secondaryButtonText,
  } = context;

  return (
    <div
      className="w-full max-w-md flex flex-col items-center gap-4 p-8 bg-background/primary rounded-lg shadow-card text-center"
      data-testid={DATA_TEST_ID}
    >
      <img
        alt={logoAlt}
        className="h-6 object-contain"
        data-testid={`${DATA_TEST_ID}-logo`}
        src={logoSrc}
      />

      {badge ? (
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-default/10 text-brand-default body-small-semibold leading-none"
          data-testid={`${DATA_TEST_ID}-badge`}
        >
          {badgeIcon}
          <span>{badge}</span>
        </div>
      ) : null}

      <h2 className="heading-5 text-text/primary">{title}</h2>

      <p className="body-base-regular text-text/secondary">{description}</p>

      <div className="flex items-center justify-center gap-3 pt-2">
        <ThemedButton
          dataTestId={`${DATA_TEST_ID}-secondary-button`}
          disabled={isSubmitting}
          onClick={onAcknowledge}
          variant="secondary"
        >
          {secondaryButtonText}
        </ThemedButton>
        <ThemedButton
          dataTestId={`${DATA_TEST_ID}-primary-button`}
          disabled={isSubmitting}
          isLoading={isSubmitting}
          onClick={onPrimaryAction}
          variant="primary"
        >
          {primaryButtonText}
        </ThemedButton>
      </div>
    </div>
  );
};
